import type { AuditHistoryContext } from "@playbookai/types";
import { auditHistorySummary } from "./audit-history-summary.js";
import { auditObjectLabel, auditPhraseFor } from "./audit-vocabulary.js";
import type { ApiClientConfig } from "./client-config.js";
import { apiCall } from "./http.js";
import { PlaybookApiError } from "./errors.js";

export type EntityAuditSource =
  | Readonly<{ kind: "human" }>
  | Readonly<{ kind: "ai"; agentId?: string }>;

/** Wire shape owned by `docs/api/playbook-be/domains/audit`. */
export interface EntityAuditWireEntry {
  /** Verified repeat-completion receipt; the audit API retains the source row. */
  readonly noop?: true;
  readonly id: string;
  readonly ts: string;
  readonly actorId: string | null;
  readonly actorName: string | null;
  /**
   * Present only when a SERVICE wrote the row. Kept separate from
   * `source.kind` because that field is human-vs-AI provenance and the audit
   * API filters on it; see the server DTO for why they must not be merged.
   */
  readonly actorKind?: "system";
  readonly service: string;
  readonly action: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly auditClass: "application" | "canvas";
  readonly source: EntityAuditSource;
  /**
   * WHAT the row is about, for the rows that name one — always a bounded
   * catalogue value, never user-authored text. The server allowlists BOTH the
   * entityType/action pair and the value itself before emitting it.
   *
   * SIX vocabularies now reach this one field, and `auditObjectLabel` resolves
   * whichever one the value belongs to: a widget kind (`problemStatement`), a
   * VSM workshop card id (`artifact-project-context`), a VSM shape kind
   * (`WORKCELL`), an arrow flow kind (`MATERIAL`), a Hoshin X-Matrix quadrant
   * (`annualObjective`), or a Hoshin workshop step (`vote`). It began as a
   * canvas per-widget field (PB-5161) and is no longer only that — the last
   * two are relational Hoshin rows rather than canvas projections. The six
   * key spaces must stay disjoint for one field to serve them all, which
   * `entity-audit.spec.ts` asserts rather than assumes.
   *
   * Absence is not a single meaning, so do not read one into it: the row may
   * be a kind the server declined to vouch for, a row type this projection
   * does not cover, or a row written before the field shipped. All three are
   * handled the same way — fall back to the phrase's plain wording.
   */
  readonly objectKind?: string;
  readonly restoredRole?: string;
  readonly historyContext?: AuditHistoryContext;
  /**
   * WHICH ONE of this actor's repeated objects this row is, on this board
   * (PB-5174). 1-based, server-derived at read time, and present only on the
   * entity-scoped read of a canvas row.
   *
   * Absence means "no number to show", NOT 1 — the tenant-wide read and the
   * single-event detail both omit it, and an older server omits it everywhere.
   */
  readonly objectOrdinal?: number;
}

export interface EntityAuditWirePage {
  readonly data: readonly EntityAuditWireEntry[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
}

/** Data-blind view model, structurally compatible with `AuditFeedEntry`. */
export interface EntityAuditFeedEntry {
  readonly id: string;
  /**
   * `system` is a service, not a person. Structurally mirrors
   * `AuditFeedEntry.actor` in packages/ui, which carries the same three-value
   * union — the two are hand-kept in step, so widening one requires the other.
   */
  readonly actor: {
    readonly name: string;
    readonly kind: "person" | "ai" | "system";
  };
  /**
   * The raw actor id, carried so a rail can resolve a display name the audit
   * API does not send for a human (`actorName` is null on every human row).
   * The one exception is the collab-projection service actor, which the server
   * names "Canvas sync" in `audit-entry.response.ts`; that id resolves to no
   * directory user, so a rail must not try to look it up.
   * Null for a system/background write, which has no identity to resolve.
   */
  readonly actorId: string | null;
  readonly action: string;
  readonly summary?: string;
  readonly historyContext?: AuditHistoryContext;
  /**
   * The wire action, unphrased. `action` is display copy now, so anything that
   * MATCHES on an action must read this instead, or rewording a phrase silently
   * breaks a matcher.
   *
   * Nothing matches on it TODAY, and it has NO consumer at all. The Revert
   * filter was its only one, and this PR removed that segment (ADR-0072,
   * 2026-09-10 amendment); the surviving `CANVAS_HISTORY_FILTERS` segments match
   * on `auditClass` alone.
   *
   * So this field IS that feature's leftover, and an earlier draft of this
   * comment claimed the opposite — that it pre-dated the segment — citing
   * `e749cb531`. That hash is this field's OWN introducing commit (27 Aug), the
   * Revert segment landed six days earlier in `92b2bfcb5` (21 Aug), and
   * `e749cb531` is what rewired the filter to `(e.rawAction ?? e.action)`. The
   * claim was inverted and is corrected rather than quietly dropped.
   *
   * It is nevertheless KEPT, and that is a judgement worth disagreeing with
   * rather than a fact: the same commit that added it turned `action` into
   * display copy, so this is now the only unphrased action reaching the client.
   * Every other field here is an id or prose. A matcher added later without it —
   * a re-added Revert segment when revert lands, a grouping, a filter — would
   * have to re-derive an action from rendered copy, which is the coupling the
   * field was created to break. Deleting it is the alternative and it is cheap to
   * do later; nothing depends on the choice except how a future matcher is
   * written.
   */
  readonly rawAction: string;
  /** Pre-formatted for DISPLAY — relative or absolute. Never sort on this. */
  readonly timestamp: string;
  /**
   * The raw wire instant, carried through UNFORMATTED because `timestamp` is
   * lossy prose ("2 days ago") and the merge in `entity-audit-merge.ts` needs a
   * real sort key. `(ts, id)` is the server's own keyset ordering (ADR-0061), so
   * ordering on the same pair is what lets two streams interleave exactly as one
   * stream would have.
   */
  readonly ts: string;
  /**
   * Carried through from the wire so a module rail can offer a Canvas/Workflow
   * filter. The feed component never branches on it — only a caller-supplied
   * filter `match` reads it (ADR-0058 `audit_class`).
   */
  readonly auditClass: "application" | "canvas";
}

/**
 * Internal collab idempotency receipts, filed under their own
 * `collab_semantic_mutation_receipt` entity type. They record that a mutation
 * command was reserved or applied — a mechanism detail, not something a person
 * did — so they are dropped from every user-facing History rail. The rows stay
 * in `audit_log` and remain visible to the tenant-wide admin audit view, which
 * is deliberately unfiltered.
 */
const INTERNAL_RECEIPT_ACTIONS: ReadonlySet<string> = new Set([
  "canvas_semantic_mutation_reserved",
  "canvas_semantic_mutation_applied",
  // Compliance placeholders, not events. `withAuditedTransaction` refuses to
  // commit without an audit row, so a store that changed NOTHING still has to
  // write one: `projection_ping` for a no-op canvas projection (an awareness
  // flush with zero added/removed/moved) and `widget_placement_noop` for a
  // placement rewritten to the position it already held. Both say "a person
  // did nothing", which is worse than silence in a history rail — a reader
  // cannot tell them from real activity. Each is commented as such at its
  // write site.
  "projection_ping",
  "widget_placement_noop",
  /* Re-granting Facilitator to the holder who already has it. Audited so the
     request leaves a trace, but it changed nothing, so it belongs with the
     other "a person did nothing" receipts rather than in a history rail —
     it was rendering as "FACILITATOR GRANT NOOP". */
  "facilitator_grant_noop",
  /* System re-derivations, not activity. `canvas_reprojected` is a recovery
     re-projection and `canvas_projection_stale_ignored` is an out-of-order
     delivery DISCARDED — no state changed at all. Both are written by the
     system actor with no recoverable human behind them, so the only line they
     could produce is "Canvas sync re-synced the canvas". */
  "canvas_reprojected",
  "canvas_projection_stale_ignored",
  /* A VSM flush that changed nothing nameable. Every object family the VSM
     canvas holds — widgets, sticky notes, artifact cards, symbols, lanes,
     arrows — now fans out per object, and the ONE remaining thing a
     `canvas_projected` flush could otherwise be carrying is a workshop phase
     or reveal written into Yjs, which `vsm-canvas.doc-type` deliberately
     DISCARDS as a legacy write (`legacy_vsm_phase_write_ignored`): the
     governed phase lives relationally and its own `phase_advanced` row is
     written on the REST route. So the row describes no state a reader can
     act on, and it rendered as the flat "edited the canvas" beside the
     specific lines for the same work. It joins the receipts rather than
     competing with them. */
  "canvas_projected",
  /* The board's own materialisation (PB-5166). Opening a new canvas in edit
     mode seeds the schema version, lanes, containers and artifact params — no
     content a person authored — so the flush carries nothing discrete. Showing
     it as activity told the reader someone edited a board they had only
     opened, which is exactly the complaint `projection_ping` above exists to
     answer. */
  "canvas_initialized",
  /* PB-5441 dev QA: VSM mechanism rows that rendered as raw keys in capitals.
     `artifact_command_replayed` is the idempotent replay of a workshop-card
     command (its first run, `artifact_command_applied`, is a person's card
     edit and is phrased, d7); `shape_updated_field_purge` is the system
     clearing a deleted data field from each shape;
     `impact_effort_scores_reset` is the retry marker of an operator script
     (`reset-vsm-impact-effort.ts`), not a board action. */
  "artifact_command_replayed",
  "shape_updated_field_purge",
  "impact_effort_scores_reset",
]);

/** `true` when a row is a user-facing history event rather than a receipt. */
export function isUserFacingAuditEntry(entry: EntityAuditWireEntry): boolean {
  return (
    !INTERNAL_RECEIPT_ACTIONS.has(entry.action) &&
    !(
      entry.entityType === "hoshin_workshop" &&
      entry.action === "completed" &&
      entry.noop === true
    )
  );
}

export interface EntityAuditPage {
  readonly entries: readonly EntityAuditFeedEntry[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
  /**
   * `(ts, id)` of the oldest row the SERVER returned for this page, taken
   * BEFORE receipts are filtered out of `entries` — `null` only when the
   * raw page was empty.
   *
   * Load-bearing for `mergeEntityAuditStreams`. `entries` can legitimately
   * be empty while `hasMore` is true, when a whole page happened to be internal
   * receipts. Without this the merge cannot tell that page apart from a stream that
   * has not answered yet, so it withholds the OTHER stream's rows every time it
   * happens. It is also the tighter bound: everything still unfetched is strictly
   * older than the raw boundary, not merely older than the last surviving row.
   */
  readonly oldestKey: { readonly id: string; readonly ts: string } | null;
}

export interface ListEntityAuditInput {
  readonly entityType: string;
  readonly entityId: string;
  readonly cursor?: string;
  readonly limit?: number;
}

export interface MapEntityAuditOptions {
  readonly currentUser?: { readonly id: string; readonly name: string | null };
  readonly now?: Date;
}

const AUDIT_PATH = "/v1/audit";

/**
 * Thin shared transport for module History/Activity feeds. Tenant and actor are
 * deliberately absent from the query: playbook-be derives both from the JWT.
 *
 * `signal` aborts the request. A caller that aborts it with a reason named
 * `TimeoutError` gets that reason back as the error, which
 * `entityAuditRetry` declines to retry (PB-5441).
 */
export async function listEntityAudit(
  config: ApiClientConfig,
  input: ListEntityAuditInput,
  signal?: AbortSignal,
): Promise<EntityAuditPage> {
  const page = await apiCall<EntityAuditWirePage>(
    config,
    "GET",
    AUDIT_PATH,
    undefined,
    {
      entityType: input.entityType,
      entityId: input.entityId,
      cursor: input.cursor,
      limit: input.limit ?? 20,
    },
    signal,
  );
  const currentUser = config.getCurrentUser?.() ?? undefined;
  const rawOldest = page.data[page.data.length - 1];
  return {
    // Receipts are dropped HERE rather than server-side so the audit API stays
    // one unfiltered contract: the admin view still sees every row.
    entries: page.data
      .filter(isUserFacingAuditEntry)
      .map((entry) =>
        mapEntityAuditEntry(entry, currentUser ? { currentUser } : {}),
      ),
    nextCursor: page.nextCursor,
    hasMore: page.hasMore,
    // From the RAW page, so a fully-filtered page still reports a boundary.
    oldestKey: rawOldest ? { id: rawOldest.id, ts: rawOldest.ts } : null,
  };
}

/**
 * The paging, retry and freshness rules for an entity-audit stream, shared by
 * every module's History rail.
 *
 * These live HERE, beside the transport, because they are properties of the
 * audit CONTRACT rather than of any one module — three feature hooks previously
 * held byte-identical copies, so one contract change had to land in three places
 * to stay correct.
 *
 * Only the rules are shared, deliberately NOT a whole options factory including
 * `queryFn`. Each hook keeps its own one-line `queryFn` calling
 * `listEntityAudit`, because that call is the seam every rail's tests mock; a
 * factory that closed over this module's own binding would be unreachable from
 * those mocks and would force each spec to re-wrap a factory to test its wiring.
 */
export const ENTITY_AUDIT_STALE_TIME_MS = 30_000;

/**
 * How often an OPEN History rail re-reads its stream.
 *
 * The rail had no refresh at all: `staleTime` alone only re-reads on a remount,
 * so a feed left open never showed a row written after it opened, and closing
 * and reopening the rail was the only way to see new activity. That reopen is a
 * remount past a stale cache — it was the workaround, not the design.
 *
 * A poll rather than an invalidation because nothing in this app writes these
 * rows: canvas history is written by the COLLAB service, after its own debounced
 * flush, so no client-side mutation exists to invalidate on and the client
 * cannot know when the flush landed. Paused in background tabs by TanStack's
 * default `refetchIntervalInBackground: false`, and the rails mount only while
 * open, so a closed rail polls nothing.
 *
 * Shorter than the stale time on purpose: 30s answers "is a remount allowed to
 * serve cache", this answers "how stale may a WATCHED feed get", and a feed
 * being watched is exactly when a slower answer is wrong.
 *
 * 5s is chosen against the canvas projection window. Incremental Yjs updates
 * are durable in the journal first, but an audit row appears only after the
 * next full snapshot commit: 2s after quiet and at most 10s while editing
 * continuously. Polling faster does not make that projection exist sooner;
 * truly simultaneous delivery needs a push from collab at commit time.
 *
 * Recorded as a decision in ADR-0072 ("An open feed refreshes itself, bounded to
 * its first page"), which also carries the per-stream consequence.
 *
 * The reader's OWN actions do not wait for this at all — see
 * `isEntityAuditRefreshEvent`.
 */
export const ENTITY_AUDIT_REFETCH_INTERVAL_MS = 5_000;

/**
 * Whether a mutation-cache event means ANY entity's history may have changed.
 *
 * Deliberately not entity-scoped: it takes no entity argument and asks only
 * whether some mutation succeeded somewhere in the app, so an unrelated write —
 * marking a notification read, saving a preference, a mutation in another
 * module — refreshes every open audit feed. That is the accepted cost of the
 * trade below, not an oversight.
 *
 * The reader's own actions should not wait out the poll: a workflow row is
 * written SYNCHRONOUSLY by the module BE inside the request, so by the time a
 * mutation resolves the row already exists and the feed is simply stale. A rail
 * refreshed here shows "created this plan" at once instead of up to 5s later.
 *
 * Keying on "any successful mutation" rather than an enumerated list is sound in
 * THIS repo specifically: rule 4 requires every state-changing endpoint to write
 * an audit row in the same transaction, so a succeeded mutation implies a row.
 * Enumerating instead would mean every new mutation silently opting out of its
 * own history, which is the failure mode that leaves a feed quietly wrong.
 *
 * Refetching on an event, unlike on a timer, is bounded by how often a person
 * acts rather than by the clock, so it does not reintroduce the per-tick page
 * amplification `entityAuditRefetchInterval` exists to bound.
 *
 * Typed structurally so this package needs no react-query dependency; the
 * shape is TanStack's `MutationCacheNotifyEvent`.
 */
export function isEntityAuditRefreshEvent(event: {
  readonly type: string;
  readonly mutation?:
    | { readonly state: { readonly status: string } }
    | undefined;
}): boolean {
  return event.type === "updated" && event.mutation?.state.status === "success";
}

/**
 * Whether a stream is still on its first page.
 *
 * The page bound of the polling policy, named separately because BOTH refresh
 * paths need it. `invalidateQueries` services a refetch by re-reading every
 * stored page, so an unbounded invalidation would make the reader who has paged
 * furthest into history the most expensive one on the system — the same
 * backwards cost curve the timer bound exists to prevent, arriving by the other
 * door.
 */
export function entityAuditIsUnpaged(query: {
  readonly state: { readonly data?: unknown };
}): boolean {
  /* `data` is narrowed at runtime rather than typed as InfiniteData because the
     two callers hand over different static types: `refetchInterval` gets the
     fully-typed query, while an `invalidateQueries` predicate gets
     `Query<unknown, ...>`. One structural signature could not satisfy both. */
  const data = query.state.data;
  const pages =
    typeof data === "object" &&
    data !== null &&
    "pages" in data &&
    Array.isArray((data as { pages: unknown }).pages)
      ? (data as { pages: readonly unknown[] }).pages.length
      : 0;
  return pages <= 1;
}

/**
 * The polling rule itself, bounded to the FIRST page.
 *
 * A bare `refetchInterval` is not safe on an infinite query: TanStack refetches
 * every stored page on each tick, so a reader who had pressed Load more three
 * times would cost four requests per tick per stream, and a merged rail runs two
 * streams. The cost scales with how much history someone has read, which is
 * exactly backwards.
 *
 * `maxPages` is the wrong lever here despite being the obvious one: it bounds
 * pages by DISCARDING the oldest, so paging down a history feed would silently
 * drop the rows already on screen. Polling is instead limited to the state where
 * it earns its cost — a rail sitting on page one, watching for new activity at
 * the top. Once the reader pages into history the feed stops polling: new rows
 * arrive above what they are reading, they have shown they want older rows, and
 * reopening the rail re-reads from the top anyway.
 */
export function entityAuditRefetchInterval(query: {
  readonly state: {
    readonly data?: { readonly pages: readonly unknown[] } | undefined;
  };
}): number | false {
  return entityAuditIsUnpaged(query) ? ENTITY_AUDIT_REFETCH_INTERVAL_MS : false;
}

/**
 * The BACKSTOP interval, for a feed that also refreshes on push.
 *
 * Same 60s the bell socket keeps behind its own push, and for the same reason:
 * Redis pub/sub has no replay, so a nudge dropped while a socket was down is
 * invisible. A slow poll turns that into a freshness cost rather than a
 * correctness one, which is why the timer is DEMOTED here and never deleted.
 */
export const ENTITY_AUDIT_PUSH_BACKSTOP_INTERVAL_MS = 60_000;

/**
 * Polling rule for a push-backed feed — a SECOND rule, deliberately not an edit
 * to `entityAuditRefetchInterval` above.
 *
 * That function has FIVE consumers: the four History rails and the KPI activity
 * card. Only the rails are push-backed. Demoting the shared rule would have
 * quietly dropped the KPI card to a 60s poll with no push to compensate — and
 * that card is the worst surface to slow down, because it is permanently
 * mounted, so a reader cannot even close and reopen it to force a read. Two
 * rules reading one constant is the cheap way to keep the two honest.
 *
 * Bounded to the first page exactly as the 5s rule is; see `entityAuditIsUnpaged`
 * for why paging must stop the timer rather than multiply it.
 */
export function entityAuditPushBackedRefetchInterval(query: {
  readonly state: {
    readonly data?: { readonly pages: readonly unknown[] } | undefined;
  };
}): number | false {
  return entityAuditIsUnpaged(query)
    ? ENTITY_AUDIT_PUSH_BACKSTOP_INTERVAL_MS
    : false;
}

/**
 * Window-focus rule for a push-backed feed, bounded to the FIRST page.
 *
 * `"always"` and not `true`: the shell's QueryClient sets
 * `refetchOnWindowFocus: false` globally, so an override is needed at all, and
 * under the 30s `staleTime` a plain `true` declines to refetch inside exactly
 * the window a dropped socket leaves a gap in.
 *
 * BOUNDED for the same reason `entityAuditRefetchInterval` is, and it is the
 * same trap by a third door: a refetch re-reads EVERY stored page, so a reader
 * five pages deep would spend five requests per stream on every focus event,
 * and a canvas rail runs two streams. A flat `"always"` shipped that; the bound
 * makes focus cost exactly one request per stream, or nothing once the reader
 * has paged into history — where new rows arrive above what they are reading
 * anyway.
 */
export function entityAuditRefetchOnWindowFocus(query: {
  readonly state: {
    readonly data?: { readonly pages: readonly unknown[] } | undefined;
  };
}): "always" | false {
  return entityAuditIsUnpaged(query) ? "always" : false;
}

/**
 * Cursor rule, annotated with the transport's own return type so a change to
 * `EntityAuditPage` cannot silently drift away from how it is paged.
 */
export function entityAuditNextPageParam(
  last: EntityAuditPage,
): string | undefined {
  return last.hasMore ? (last.nextCursor ?? undefined) : undefined;
}

/**
 * A refusal that will not change on a retry.
 *
 * ADR-0061 conceals a deny, an unregistered entity type and a cross-tenant
 * entity all as 403/404, so one of those is the server ANSWERING about scope
 * rather than an outage — definitive until the caller's scope itself changes.
 *
 * Narrower than `retryTransientRead`'s terminal set in `retry.ts`, and
 * deliberately so: that one also treats 401 as terminal, which for an audit read
 * is an expired session the shell already handles, not a scope answer this feed
 * should act on.
 *
 * One caller today — `entityAuditRetry` below, deciding whether to ASK again.
 * The second lands in PB-5370 PR2, where each History hook decides whether to
 * OFFER a Retry at all: a button on a definitive refusal could only ever repeat
 * it. Named rather than left as an inline status test precisely so those two
 * decisions cannot drift apart once the second exists.
 */
export function isDefinitiveAuditRefusal(error: unknown): boolean {
  return (
    error instanceof PlaybookApiError &&
    (error.status === 403 || error.status === 404)
  );
}

/**
 * A read the caller gave up on because it took too long: the abort reason a
 * History hook's timeout uses, `DOMException(…, "TimeoutError")`. Matched by
 * NAME, so it is told apart from a cancellation (`AbortError`) without relying
 * on which `DOMException` class the runtime ships.
 */
function isEntityAuditTimeout(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { readonly name?: unknown }).name === "TimeoutError"
  );
}

/**
 * A 403/404 is deliberate concealment (ADR-0061) and definitive until scope
 * changes, so retrying only repeats the refusal.
 *
 * A timeout is not retried either (PB-5441): each attempt would wait the full
 * timeout again, so two retries would hold the rail on placeholders three
 * times as long before it can offer the reader a Retry of their own.
 */
export function entityAuditRetry(
  failureCount: number,
  error: unknown,
): boolean {
  return (
    !isDefinitiveAuditRefusal(error) &&
    !isEntityAuditTimeout(error) &&
    failureCount < 2
  );
}

/**
 * Tenant + user + auth-generation scoped key: the shell QueryClient survives
 * auth transitions.
 *
 * The generation is what the tenant and actor ids CANNOT cover. Both ids in the
 * key already stop one tenant's or one user's history being served to another,
 * so the remaining hole is narrow but real: the SAME user in the SAME tenant
 * whose privileges are narrowed mid-session — a membership removed while a
 * History rail is open — keeps being served the cached feed, because nothing in
 * the key changed. `authSessionGeneration` moves on such a change, so the key
 * moves with it and the next read is a miss rather than a stale hit.
 *
 * This mirrors what `solveScopePrefix`, `vsmScopePrefix` and `hoshinScopePrefix`
 * carry; those three each document that swapping to this key would be a silent
 * regression. That was true, and this is what closes the gap for the two
 * consumers that legitimately use the shared key — `lama-feature` and
 * `kpi-feature` — rather than asking each to grow a fourth copy of the prefix.
 */
export function entityAuditQueryKey(
  config: ApiClientConfig,
  entityType: string,
  entityId: string,
): readonly string[] {
  const scope = config.authScope?.getSnapshot() ?? null;
  return [
    "audit",
    "entity",
    scope?.tenantId ?? "no-tenant",
    config.getCurrentUser?.()?.id ?? scope?.actorUserId ?? "anonymous",
    /* No scope source at all is its own bucket, distinct from any real
       generation, so a pre-auth read can never share a cache entry with an
       authenticated one. */
    scope?.authSessionGeneration ?? "no-generation",
    entityType,
    entityId,
  ] as const;
}

export function mapEntityAuditEntry(
  entry: EntityAuditWireEntry,
  options: MapEntityAuditOptions = {},
): EntityAuditFeedEntry {
  return {
    id: entry.id,
    actor: actorFor(entry, options.currentUser),
    actorId: entry.actorId,
    /* Phrase first, humanised raw action second. The vocabulary is open — a
       module can add an action at any time — so an unmapped pair must still
       render as something readable rather than blank. */
    action: auditActionCopy(entry) ?? humanizeAuditAction(entry.action),
    ...restorationSummary(entry),
    ...(entry.historyContext
      ? {
          historyContext: entry.historyContext,
          summary: auditHistorySummary(entry.historyContext),
        }
      : {}),
    rawAction: entry.action,
    timestamp: formatAuditTimestamp(entry.ts, options.now),
    ts: entry.ts,
    auditClass: entry.auditClass,
  };
}

/**
 * The phrased copy for one row, or `undefined` when the pair is unmapped.
 *
 * Names the OBJECT when three things all hold (PB-5161): the server allowlisted
 * an `objectKind` onto this row, the phrase has a `withObject` form, and the
 * shared catalogues can label that kind. Any one of them missing falls back to
 * the phrase's plain `predicate` — the wording every row had before — so a new
 * catalogue kind, an older server, or an unmapped pair degrades quietly instead
 * of rendering a raw enum or a blank line.
 */
function auditActionCopy(entry: EntityAuditWireEntry): string | undefined {
  const context = entry.historyContext;
  if (
    entry.entityType === "hoshin_xmatrix_item" &&
    entry.action === "xmatrix_items_reordered" &&
    context?.reorderedItems?.length
  ) {
    const [first, ...others] = context.reorderedItems;
    return `reordered “${first!.title}”${others.length ? ` and ${others.length} other X-Matrix item${others.length === 1 ? "" : "s"}` : ""}`;
  }
  if (context?.parkingAction)
    return `${context.parkingAction} IDEA${context.referenceNumber ? ` #${context.referenceNumber}` : ""}`;
  if (context?.workOperation)
    return `${context.workOperation} ${context.workKind === "solve" ? "SOLVE" : context.workKind === "vsm" ? "VSM" : "work"}${context.workOperation === "started" ? " and linked it to the plan" : ""}`;
  if (context?.cleanup === "blank_note")
    return context.referenceNumber
      ? `automatically removed blank IDEA #${context.referenceNumber}`
      : "automatically removed a blank idea";
  if (context?.cleanup === "empty_theme")
    return "automatically removed an empty theme";
  if (context?.referenceNumber && entry.entityType === "hoshin_workshop_idea") {
    const verb = entry.action === "idea_deleted" ? "removed" : "created";
    return `${verb} IDEA #${context.referenceNumber}`;
  }
  const found = auditPhraseFor(entry.entityType, entry.action);
  if (!found) return undefined;
  const label =
    entry.objectKind === undefined
      ? undefined
      : auditObjectLabel(entry.objectKind);

  /* PB-5174 — the COUNTED form first, when this is not the actor's first one.
     Deliberately from 2 upward: the first of anything reads better unnumbered
     ("added a sticky note"), and a lone "1" would imply more exist. A reader
     scanning the rail newest-first therefore sees 3, 2, then the unnumbered
     first — which is what makes repeated rows tellable apart at all.

     Tried before `withObject` because the counted form has to restate the
     whole clause, not append to it: "added a sticky note" + " 2" would keep the
     article, and the phrase that owns the wording is the only thing that can
     drop it.

     `Number.isInteger` mirrors the server's own guard in `toAuditEntry`, and it
     earns its place rather than restating it. The value is a `ROW_NUMBER()`, so
     a fraction cannot come from the query that produces it — but this is the
     DEFENSIVE end of the wire, where the point is to hold against a server this
     client did not ship with. `>= 2` alone already rejected `0`, `-1` and `1.5`,
     which is why a test over exactly those three passed without any integrality
     check; `2.5` would have rendered "added sticky note 2.5". */
  if (
    typeof entry.objectOrdinal === "number" &&
    Number.isInteger(entry.objectOrdinal) &&
    entry.objectOrdinal >= 2 &&
    found.withCount !== undefined
  ) {
    return found.withCount(entry.objectOrdinal, label);
  }
  if (label === undefined || found.withObject === undefined) {
    return found.predicate;
  }
  return found.withObject(label);
}

function actorFor(
  entry: EntityAuditWireEntry,
  currentUser: MapEntityAuditOptions["currentUser"],
): EntityAuditFeedEntry["actor"] {
  if (entry.source.kind === "ai") {
    return {
      name:
        entry.source.agentId === "player-ai"
          ? "Player AI"
          : entry.source.agentId || "AI",
      kind: "ai",
    };
  }
  /* Checked BEFORE the name branch: a service HAS a name ("Canvas sync"), so
     testing the name first would classify it as a person and give it initials. */
  if (entry.actorKind === "system") {
    return { name: entry.actorName ?? "System", kind: "system" };
  }
  if (entry.actorName) return { name: entry.actorName, kind: "person" };
  if (entry.actorId && entry.actorId === currentUser?.id) {
    return { name: currentUser.name || "You", kind: "person" };
  }
  if (entry.actorId) {
    /* Deliberately NOT `User ${id.slice(0,8)}`: every seeded id shares a
       leading run, so that rendered as "User 00000000" for different people at
       once, and it leaked an id fragment into a surface that shows no ids. An
       unresolved actor is a member whose name the roster did not return —
       usually someone who has since left. */
    return { name: "A member", kind: "person" };
  }
  return { name: "System", kind: "person" };
}

/**
 * The fallback for an unmapped action, in sentence case: "Map proposed change
 * applied". It used to upper-case the key, which read as a shouted error code
 * beside the phrased rows (PB-5441 dev QA).
 */
export function humanizeAuditAction(action: string): string {
  const words = action
    .replace(/[._-]+/g, " ")
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function formatAuditTimestamp(value: string, now = new Date()): string {
  const timestamp = new Date(value);
  if (!Number.isFinite(timestamp.getTime())) return value;
  const elapsed = Math.max(0, now.getTime() - timestamp.getTime());
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} ${days === 1 ? "day" : "days"} ago`;
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(timestamp);
}

// ---------- actor-name resolution (PB-4758 follow-up) ----------

/**
 * Server cap for one `identities/resolve` request. `identityBody` in
 * `common/directory-request.validators.ts` rejects an empty array and anything
 * over 50 with a 400, so callers must both batch AND skip the empty call.
 */
export const IDENTITY_RESOLVE_BATCH_SIZE = 50;

/** Split ids at the server cap. Empty input yields NO batches, never `[[]]`. */
export function batchActorIdsForResolve(
  userIds: readonly string[],
): string[][] {
  const batches: string[][] = [];
  for (
    let index = 0;
    index < userIds.length;
    index += IDENTITY_RESOLVE_BATCH_SIZE
  ) {
    batches.push(userIds.slice(index, index + IDENTITY_RESOLVE_BATCH_SIZE));
  }
  return batches;
}

/**
 * The person actor ids in a feed that still need a display name.
 *
 * `/v1/audit` sends `actorName: null` on every HUMAN row BY DESIGN — the audit
 * row stores only `actor_id` and "name resolution is left to the FE directory"
 * (`audit-entry.response.ts`, which names only the collab system actor) — so a
 * rail that does not resolve renders the mapper's "A member" fallback for
 * everyone.
 *
 * Two exclusions, both deliberate. AI rows carry their agent name already, so
 * they are not identities to look up. And `currentUserId` is skipped because the
 * session already supplies their display identity. A feature may personalize
 * that as "You"; resolving it would overwrite the feature’s display choice.
 *
 * Deduplicated, because one person usually authored many rows and the cap is
 * per REQUEST, not per row.
 */
export function auditActorIdsNeedingNames(
  entries: readonly EntityAuditFeedEntry[],
  currentUserId?: string | null,
): string[] {
  const ids = new Set<string>();
  for (const entry of entries) {
    const ownerChange = entry.historyContext?.ownerChange;
    if (ownerChange?.beforeUserId) ids.add(ownerChange.beforeUserId);
    if (ownerChange?.afterUserId) ids.add(ownerChange.afterUserId);
    if (entry.actor.kind !== "person") continue;
    const actorId = entry.actorId;
    if (!actorId) continue;
    if (currentUserId && actorId === currentUserId) continue;
    ids.add(actorId);
  }
  return [...ids];
}

/**
 * Apply resolved display names to a feed.
 *
 * Returns the SAME array when nothing resolved, so a rail re-render is not
 * triggered by a lookup that answered nothing — which is the common case for a
 * system actor or a former member the roster no longer holds. An id absent from
 * the map keeps its placeholder rather than blanking: a row with an unknown
 * author is still a true row, and ADR-0061's contract note is explicit that a
 * null name must never stop a row rendering.
 */
export function withResolvedAuditActorNames(
  entries: readonly EntityAuditFeedEntry[],
  displayNameByUserId: ReadonlyMap<string, string>,
  currentUserId?: string | null,
): readonly EntityAuditFeedEntry[] {
  if (displayNameByUserId.size === 0) return entries;
  let changed = false;
  const mapped = entries.map((entry) => {
    const resolved =
      entry.actor.kind === "person" &&
      entry.actorId &&
      entry.actorId !== currentUserId
        ? displayNameByUserId.get(entry.actorId)
        : undefined;
    const summary = entry.historyContext
      ? auditHistorySummary(entry.historyContext, displayNameByUserId)
      : entry.summary;
    if (
      (resolved === undefined || resolved === entry.actor.name) &&
      summary === entry.summary
    )
      return entry;
    changed = true;
    return {
      ...entry,
      ...(summary === undefined ? {} : { summary }),
      actor: { ...entry.actor, name: resolved ?? entry.actor.name },
    };
  });
  return changed ? mapped : entries;
}

function restorationSummary(entry: EntityAuditWireEntry): { summary?: string } {
  const pairs = [
    "solve_membership|solve.access_restored",
    "vsm_membership|vsm.access_restored",
    "lama_campaign_membership|lama.access_restored",
  ];
  const labels: Readonly<Record<string, string>> = {
    owner: "Owner",
    collaborator: "Collaborator",
    viewer: "Viewer",
    campaign_owner: "Owner",
    campaign_analyst: "Analyst",
    campaign_viewer: "Viewer",
  };
  const role = entry.restoredRole;
  return pairs.includes(`${entry.entityType}|${entry.action}`) &&
    role &&
    Object.hasOwn(labels, role)
    ? { summary: `No access → ${labels[role]}` }
    : {};
}
